import type React from "react";
import type { Tenant, TenantStatus } from "../types/index.js";

interface TenantCatalogTableProps {
  tenants: Tenant[];
  onSelectTenant: (tenant: Tenant) => void;
  onToggleStatus: (tenantId: string, currentStatus: TenantStatus) => void;
}

const statusColors: Record<TenantStatus, { bg: string; text: string }> = {
  active: { bg: "#064e3b", text: "#34d399" },
  provisioning: { bg: "#78350f", text: "#fbbf24" },
  suspended: { bg: "#7f1d1d", text: "#f87171" },
  retired: { bg: "#374151", text: "#9ca3af" },
};

export const TenantCatalogTable: React.FC<TenantCatalogTableProps> = ({
  tenants,
  onSelectTenant,
  onToggleStatus,
}) => {
  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "16px", color: "#f8fafc" }}>
            Tenant Fleet Registry
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
            Authoritative SaaS catalog stored in D1. Maps WorkOS orgs and custom domains to tenant DB secret refs.
          </p>
        </div>
        <span
          style={{
            fontSize: "12px",
            color: "#94a3b8",
            backgroundColor: "#1e293b",
            padding: "4px 10px",
            borderRadius: "6px",
          }}
        >
          Total Tenants: {tenants.length}
        </span>
      </div>

      <div
        style={{
          backgroundColor: "#0f172a",
          border: "1px solid #1e293b",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "left",
            fontSize: "13px",
            color: "#e2e8f0",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#1e293b",
                color: "#94a3b8",
                borderBottom: "1px solid #334155",
              }}
            >
              <th style={{ padding: "12px 16px" }}>Slug / Host Label</th>
              <th style={{ padding: "12px 16px" }}>Tenant UUID</th>
              <th style={{ padding: "12px 16px" }}>Lifecycle Status</th>
              <th style={{ padding: "12px 16px" }}>Database SecretRef</th>
              <th style={{ padding: "12px 16px" }}>Schema Version</th>
              <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => {
              const colors = statusColors[tenant.status] || {
                bg: "#1e293b",
                text: "#e2e8f0",
              };
              return (
                <tr
                  key={tenant.id}
                  style={{
                    borderBottom: "1px solid #1e293b",
                    transition: "background 0.15s ease",
                  }}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                    {tenant.slug}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <code
                      style={{
                        backgroundColor: "#1e293b",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontSize: "11px",
                        color: "#38bdf8",
                      }}
                    >
                      {tenant.id}
                    </code>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "9999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor: colors.bg,
                        color: colors.text,
                        textTransform: "capitalize",
                      }}
                    >
                      {tenant.status}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {tenant.neonSecretRef ? (
                      <code
                        style={{
                          backgroundColor: "#1e293b",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "11px",
                          color: "#a78bfa",
                        }}
                      >
                        {tenant.neonSecretRef}
                      </code>
                    ) : (
                      <span style={{ color: "#64748b", fontStyle: "italic" }}>
                        (withheld: {tenant.status})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", color: "#94a3b8" }}>
                    v{tenant.schemaVersion}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => onSelectTenant(tenant)}
                      style={{
                        padding: "4px 8px",
                        marginRight: "6px",
                        fontSize: "12px",
                        backgroundColor: "#1e293b",
                        color: "#38bdf8",
                        border: "1px solid #334155",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Details
                    </button>
                    {tenant.status === "active" && (
                      <button
                        type="button"
                        onClick={() => onToggleStatus(tenant.id, "active")}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          backgroundColor: "#450a0a",
                          color: "#f87171",
                          border: "1px solid #7f1d1d",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Suspend
                      </button>
                    )}
                    {tenant.status === "suspended" && (
                      <button
                        type="button"
                        onClick={() => onToggleStatus(tenant.id, "suspended")}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          backgroundColor: "#064e3b",
                          color: "#34d399",
                          border: "1px solid #065f46",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Resume
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
